import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { TextalignRight, Link21, MessageText } from "iconsax-reactjs";
import UploadCard from "../../commons/UploadCard";
import InputFiel from "../../commons/InputFiel";
import Button from "../../commons/Button";
import PersianCalendar from "../../commons/PersianCalender";
import type { DayValue } from "@hassanmojab/react-modern-calendar-datepicker";
import { getImageUrl } from "../../../services/file/getImageUrl";
import { jalaliPartsToIso, isoToJalaliParts } from "../../../utils/jalaliDate";
import {
  createTopBanner,
  createContentBanner,
} from "../../../services/webServices/banners/post";
import {
  updateTopBanner,
  updateContentBanner,
} from "../../../services/webServices/banners/patch";
import {
  CONTENT_TYPE_LABELS,
  type BannerSection,
  type ContentType,
  type ContentBanner,
  type TopBanner,
} from "../../../services/webServices/banners/types";

const DESKTOP_CROP_ASPECT = 1920 / 55;
const PHONE_CROP_ASPECT = 360 / 50;
const FLOATING_CROP_ASPECT = 245 / 138;

const isValidUrl = (value: string): boolean => {
  try {
    return Boolean(new URL(value));
  } catch {
    return false;
  }
};

type BannerFormProps = {
  section: BannerSection;
  contentType: ContentType;
  editingBanner: TopBanner | ContentBanner | null;
  onSaved: () => void;
  onCancel: () => void;
};

export default function BannerForm({
  section,
  contentType,
  editingBanner,
  onSaved,
  onCancel,
}: BannerFormProps) {
  const isTop = section === "top";
  const isEditing = !!editingBanner;

  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [contentId, setContentId] = useState("");
  const [desktopFile, setDesktopFile] = useState<File | null>(null);
  const [phoneFile, setPhoneFile] = useState<File | null>(null);
  const [cardKey, setCardKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!editingBanner) {
      setTitle("");
      setUrl("");
      setDescription("");
      setIsActive(true);
      setExpiresAt(null);
      setContentId("");
      setDesktopFile(null);
      setPhoneFile(null);
      setCardKey((k) => k + 1);
      return;
    }

    setTitle(editingBanner.title);
    setUrl(editingBanner.url);
    setDescription(editingBanner.description);
    setIsActive(editingBanner.is_active);
    setDesktopFile(null);
    setPhoneFile(null);
    setCardKey((k) => k + 1);

    if (isTop) {
      setExpiresAt((editingBanner as TopBanner).expires_at);
    } else {
      setContentId(String((editingBanner as ContentBanner).content_id));
    }
  }, [editingBanner, isTop]);

  const handleSubmit = async () => {
    if (!title || !url) {
      toast.info("لطفاً عنوان و لینک بنر را وارد کنید");
      return;
    }

    if (!isValidUrl(url)) {
      toast.info(
        "لینک بنر باید یک آدرس کامل باشد (مثال: https://sepehrjou.ir/courses)",
      );
      return;
    }

    const hasExistingImage = !!editingBanner?.desktop_image;
    if (!desktopFile && !hasExistingImage) {
      toast.info("لطفاً تصویر اصلی بنر را انتخاب کنید");
      return;
    }

    if (!isTop && !contentId) {
      toast.info("لطفاً شناسه محتوا را وارد کنید");
      return;
    }

    setIsSubmitting(true);

    let success: boolean;
    try {
      const shared = {
        title,
        url,
        description,
        is_active: isActive,
        desktopImageFile: desktopFile!,
        phoneImageFile: phoneFile,
      };

      if (isEditing && editingBanner) {
        if (isTop) {
          success = await updateTopBanner({
            uuid: editingBanner.uuid,
            originalBanner: editingBanner as TopBanner,
            ...shared,
            expires_at: expiresAt,
          });
        } else {
          success = await updateContentBanner({
            uuid: editingBanner.uuid,
            contentType,
            originalBanner: editingBanner as ContentBanner,
            ...shared,
            content_id: Number(contentId),
          });
        }
      } else if (isTop) {
        success = await createTopBanner({
          banner_type: contentType,
          ...shared,
          expires_at: expiresAt,
        });
      } else {
        success = await createContentBanner({
          contentType,
          ...shared,
          content_id: Number(contentId),
        });
      }

      if (success) {
        toast.success(
          isEditing ? "بنر با موفقیت ویرایش شد" : "بنر با موفقیت ثبت شد",
        );
        onSaved();
      } else {
        toast.error("ذخیرهٔ بنر با مشکل مواجه شد");
      }
    } catch {
      toast.error("خطا در ذخیره بنر");
    } finally {
      setIsSubmitting(false);
    }
  };

  const desktopInitial = editingBanner?.desktop_image
    ? {
        type: "image" as const,
        previewUrl: getImageUrl(editingBanner.desktop_image),
      }
    : undefined;
  const phoneInitial = editingBanner?.phone_image
    ? {
        type: "image" as const,
        previewUrl: getImageUrl(editingBanner.phone_image),
      }
    : undefined;

  return (
    <div className="w-full bg-white border rounded-2xl card-shadow p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-700">
          {isTop ? "بنر بالایی" : "بنر شناور"}
        </h2>
        <div className="flex gap-2">
          <Button
            variant="gray"
            style="solid"
            size="md"
            text="انصراف"
            loading={false}
            disabled={isSubmitting}
            onClick={onCancel}
          />
          <Button
            variant="primary"
            style="solid"
            size="md"
            text={isEditing ? "ویرایش" : "ثبت بنر"}
            loading={isSubmitting}
            disabled={false}
            onClick={handleSubmit}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8">
        <div className="flex flex-col gap-6">
          <UploadCard
            key={`desktop-${cardKey}`}
            title="تصویر دسکتاپ"
            description="نسخه نمایش دسکتاپ بنر"
            helperText={
              isTop ? "نسبت پیشنهادی 1920/55" : "نسبت پیشنهادی 245/138"
            }
            mode="image"
            cropAspect={isTop ? DESKTOP_CROP_ASPECT : FLOATING_CROP_ASPECT}
            initialValue={desktopInitial}
            onChange={setDesktopFile}
          />
          <UploadCard
            key={`phone-${cardKey}`}
            title="تصویر موبایل"
            description="نسخه نمایش موبایل بنر"
            helperText={
              isTop ? "نسبت پیشنهادی 360/50" : "نسبت پیشنهادی 245/138"
            }
            mode="image"
            cropAspect={isTop ? PHONE_CROP_ASPECT : FLOATING_CROP_ASPECT}
            initialValue={phoneInitial}
            onChange={setPhoneFile}
          />
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <h3 className="flex items-center gap-1 text-gray-600 font-medium text-base leading-7">
              <TextalignRight size="20" />
              عنوان بنر
            </h3>
            <InputFiel
              as="input"
              size="lg"
              value={title}
              placeholder="مثلاً جشنواره تخفیف زمستانه"
              maxLength={50}
              disabled={false}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <h3 className="flex items-center gap-1 text-gray-600 font-medium text-base leading-7">
              <Link21 size="20" />
              لینک محتوا
            </h3>
            <InputFiel
              as="input"
              size="lg"
              value={url}
              placeholder="https://sepehrjou.ir/courses"
              maxLength={255}
              disabled={false}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <h3 className="flex items-center gap-1 text-gray-600 font-medium text-base leading-7">
              <MessageText size="20" />
              توضیح بنر
            </h3>
            <InputFiel
              as="textarea"
              size="lg"
              value={description}
              placeholder="توضیح کوتاه درباره محتوای بنر"
              maxLength={150}
              rows={3}
              disabled={false}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <label className="flex items-center gap-3 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 rounded text-primaryVar-500 focus:ring-primaryVar-500"
            />
            بنر فعال است
          </label>

          {isTop && (
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-600 font-medium">
                تاریخ انقضا (اختیاری)
              </label>
              <div className="  rounded-lg p-2">
                <PersianCalendar
                  value={isoToJalaliParts(expiresAt)}
                  onChange={(day: DayValue) =>
                    setExpiresAt(day ? jalaliPartsToIso(day) : null)
                  }
                />
              </div>
            </div>
          )}

          {!isTop && (
            <div className="flex flex-col gap-2">
              <h3 className="flex items-center gap-1 text-gray-600 font-medium text-base leading-7">
                <MessageText size="20" />
                {`شناسه ${CONTENT_TYPE_LABELS[contentType]}`}
              </h3>
              <InputFiel
                as="input"
                size="lg"
                value={contentId}
                type="number"
                placeholder="مثلاً 15"
                maxLength={50}
                disabled={false}
                onChange={(e) => setContentId(e.target.value)}
              />
              <p className="font-semibold text-xs text-gray-400">
                شناسهٔ محتوای مرتبط (عددی) را وارد کنید
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
